import { AnimatePresence, motion } from 'framer-motion'
import { EyeOff, MessageCircle, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import type { OrderMessageWithUser } from 'src/api/db/apiOrderMessage'
import { useChatByQuoteId } from 'src/api/db/apiChat'
import { useRequestChatPresence } from 'src/api/db/apiRequestPresence'
import type { ChatMessage } from 'src/shared/components/SaraChat/types/chatMessage'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Tooltip } from 'src/shared/components/ui/tooltip'
import { cn } from 'src/shared/components/ui/utils'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { ONBOARDING_ELEMENT_IDS } from 'src/shared/hooks/useOnboarding'
import { formatChatCostUsd, getChatCostUsd } from 'src/shared/utils/chatCost'
import { useRequestContext } from '../hooks/RequestContext'
import type { RequestTabId } from '../types'
import { useRequestChatSidebarResize } from './Chat/hooks'
import { RequestChatView } from './RequestChatView'
import {
  RequestMessagesTab,
  type RequestChatChannel,
} from './RequestMessagesTab'

const ChatChannelTab = ({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={cn(
      'inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium transition-colors',
      active
        ? 'bg-white text-foreground shadow-sm'
        : 'text-muted-foreground hover:text-foreground'
    )}
  >
    {children}
  </button>
)

const ChatOnlineIcon = () => (
  <span className="relative flex items-center justify-center">
    <MessageCircle className="h-4 w-4 text-foreground" strokeWidth={2.25} />
    <span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center">
      <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400 opacity-75 [animation-duration:2s]" />
      <span className="relative flex h-2.5 w-2.5 items-center justify-center rounded-full bg-white shadow-sm">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
      </span>
    </span>
  </span>
)

export type RequestChatSidebarDemo = {
  title?: string
  saraMessages: Array<{ message: ChatMessage; isPrivate: boolean }>
  messages: OrderMessageWithUser[]
  isSaraLoading?: boolean
  pingCta?: ReactNode
  composer: ReactNode
}

export const RequestChatSidebar = ({
  activeTab,
  demo,
}: {
  activeTab?: RequestTabId
  demo?: RequestChatSidebarDemo
}) => {
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const { quote } = useRequestContext()
  const { chat } = useChatByQuoteId(demo ? undefined : quote?.id)
  const chatCostUsd = isAdmin ? getChatCostUsd(chat?.message) : null
  const isDemoChat = demo != null
  const [isOpen, setIsOpen] = useState(
    () => activeTab !== 'checkout' && activeTab !== 'delivery'
  )
  const [chatChannel, setChatChannel] = useState<RequestChatChannel>('customer')
  const showAdminTabs = isAdmin && !isDemoChat
  const { width, isResizing, startResize } = useRequestChatSidebarResize()
  useRequestChatPresence({
    quotePublicId: quote?.public_id ?? undefined,
    enabled: isOpen && !isDemoChat && !isAdmin,
  })
  const chatTitle =
    demo?.title?.trim() || chat?.name?.trim() || t('Messages', 'Meddelanden')

  useEffect(() => {
    if (activeTab === 'checkout' || activeTab === 'delivery') {
      setIsOpen(false)
      return
    }
    if (isDemoChat && activeTab === 'explore') {
      setIsOpen(true)
    }
  }, [activeTab, isDemoChat])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if a dialog/popover already handled Escape
      if (event.key !== 'Escape' || event.defaultPrevented) return
      setIsOpen(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  return (
    <>
      {!isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 22 }}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
          className="absolute top-3 right-3 z-50"
        >
          <Tooltip content={chatTitle} side="bottom">
            <Button
              variant="glass"
              size="icon"
              className="group h-10 w-10 rounded-full border-gray-200/80 bg-white/80 p-0 shadow-lg backdrop-blur-md hover:bg-white/95 hover:shadow-xl"
              onClick={() => setIsOpen(true)}
              id={ONBOARDING_ELEMENT_IDS.requestChat}
              aria-label={t('Open messages', 'Öppna meddelanden')}
            >
              <span className="transition-transform duration-300 ease-out group-hover:-rotate-12 group-hover:scale-110">
                <ChatOnlineIcon />
              </span>
            </Button>
          </Tooltip>
        </motion.div>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute inset-x-3 z-40 max-md:!w-auto max-md:!max-w-none max-md:top-14 max-md:bottom-24 md:inset-y-3 md:left-auto md:right-3 md:max-w-[calc(100%-24px)]"
            style={{ width }}
          >
            <Column
              className={`relative h-full min-h-0 overflow-hidden rounded-xl border border-gray-200 bg-white/95 shadow-lg backdrop-blur-md ${
                isResizing ? 'select-none' : ''
              }`}
            >
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t(
                  'Resize messages panel',
                  'Ändra storlek på meddelandepanel'
                )}
                className="absolute inset-y-0 left-0 z-10 hidden w-2 -translate-x-1/2 cursor-col-resize touch-none md:block"
                onMouseDown={startResize}
              >
                <div
                  className={`absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gray-200 transition-colors ${
                    isResizing ? 'bg-primary/60' : 'hover:bg-primary/40'
                  }`}
                />
              </div>

              <Column
                className={`z-20 gap-2 rounded-t-xl border-b border-white/60 bg-white/70 px-3 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset] backdrop-blur-xl ${
                  showAdminTabs
                    ? 'shrink-0 pt-3 pb-1.5'
                    : 'absolute inset-x-0 top-0 py-3'
                }`}
              >
                <Row className="items-center justify-between gap-3">
                  <Row className="min-w-0 flex-1 items-center gap-2.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200/80 bg-white/80 shadow-lg backdrop-blur-md">
                      <ChatOnlineIcon />
                    </div>
                    <Column className="min-w-0 gap-0.5">
                      <span className="truncate text-sm font-semibold tracking-[-0.01em] text-foreground">
                        {chatTitle}
                      </span>
                      <span className="truncate text-[11px] font-medium text-muted-foreground">
                        {chatChannel === 'admin'
                          ? t('Hidden from the customer', 'Dolt för kunden')
                          : t(
                              'You and the Klayder team',
                              'Du och Klayder-teamet'
                            )}
                        {chatCostUsd != null
                          ? ` · ${formatChatCostUsd(chatCostUsd)}`
                          : ''}
                      </span>
                    </Column>
                  </Row>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:bg-black/5 hover:text-foreground"
                    onClick={() => setIsOpen(false)}
                    aria-label={t('Close messages', 'Stäng meddelanden')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </Row>
                {showAdminTabs && (
                  <Row
                    role="tablist"
                    aria-label={t('Chat channel', 'Chattkanal')}
                    className="w-fit rounded-full bg-black/[0.04] p-0.5"
                  >
                    <ChatChannelTab
                      active={chatChannel === 'customer'}
                      onClick={() => setChatChannel('customer')}
                    >
                      {t('Customer', 'Kund')}
                    </ChatChannelTab>
                    <ChatChannelTab
                      active={chatChannel === 'admin'}
                      onClick={() => setChatChannel('admin')}
                    >
                      <EyeOff className="h-3 w-3 opacity-70" aria-hidden />
                      {t('Admin', 'Admin')}
                    </ChatChannelTab>
                  </Row>
                )}
              </Column>

              <Column className="min-h-0 flex-1 overflow-hidden">
                {demo ? (
                  <RequestChatView
                    messages={demo.messages}
                    saraMessages={demo.saraMessages}
                    isSaraLoading={demo.isSaraLoading}
                    quoteId={quote?.id}
                    composer={demo.composer}
                    pingCta={demo.pingCta}
                  />
                ) : (
                  <RequestMessagesTab channel={chatChannel} />
                )}
              </Column>
            </Column>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
